import { countProductsPerOption } from './applyProductFilter';
import { contributionOf } from './contributionOf';
import { groupFilterOptions } from './groupFilterOptions';
import {
  offerTitleOf,
  optionKeyOf,
  providerNameOf,
  providerUidOf,
  type FilterCategory,
  type FilterOption,
  type FilterableProduct,
  type FilterType,
} from './productFilterTypes';

// Pinned locale: the server and the browser must order Han labels identically
// or hydration mismatches (docs/sort-and-filter.md §5d).
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

/**
 * Options for a derived group, read off the products on the page rather than
 * authored (CMS-1903 R2.1 / R4.2): adding a product with a new provider or loan
 * type adds the option with no content change.
 *
 * Two rules from the ticket are load-bearing here:
 *
 * - An option is only produced when a product actually carries the value, so a
 *   group never offers a filter that would return nothing (R4.2).
 * - A group whose source yields nothing produces no options at all, which is
 *   what lets the panel drop the group rather than render it empty (R1.4).
 *   That is a group with *nothing to show*, not one whose values currently
 *   match nothing: those render greyed and stay in the DOM (CMS-1542 R4.4).
 */

const labelFor = (
  product: FilterableProduct,
  filterType: FilterType | undefined,
  value: string,
): string | undefined => {
  if (filterType === 'PROVIDERS') {
    return providerUidOf(product) === value
      ? providerNameOf(product)
      : undefined;
  }
  // The uid is an internal campaign code; the reader sees the offer's title
  // (BRD FR-3.5.4, CMS-1542 R6.4).
  if (filterType === 'OFFERS') return offerTitleOf(product, value);
  return undefined;
};

// Lives beside `contributionOf`, which reads it; re-exported because the
// eligibility check reads the same attribute values (CMS-1905).
export { attributeValuesOf } from './contributionOf';

export const deriveFilterOptions = (
  category: FilterCategory,
  products: readonly FilterableProduct[],
): FilterOption[] => {
  if (!category.autoGenerateValues) return category.options;

  const byValue = new Map<string, FilterOption>();
  products.forEach((product) => {
    contributionOf(product, category).forEach((value) => {
      const existing = byValue.get(value);
      const label = labelFor(product, category.autoGenerateFrom, value);
      if (!existing) {
        // Stamped with the group's source so a derived option matches the same
        // way an authored one does, without the matcher reaching back up.
        byValue.set(value, {
          value,
          label,
          filterType: category.autoGenerateFrom,
        });
      } else if (!existing.label && label) {
        // First product to carry a name wins; a later one does not overwrite it.
        byValue.set(value, { ...existing, label });
      }
    });
  });

  const options = [...byValue.values()];
  // Alphabetical by what the reader sees, so the list stays stable as products
  // are added (R4.2.1). Values with no label sort by value.
  return options.sort((a, b) =>
    collator.compare(a.label ?? a.value, b.label ?? b.value),
  );
};

/**
 * Orders rows by `valueSorting` (docs/sort-and-filter.md §5d). Counts come from
 * the catalogue, not the selection, so rows do not jump while filtering; derived
 * categories are already alphabetical, which is their `MANUAL`.
 */
const sortFilterOptions = (
  category: FilterCategory,
  options: FilterOption[],
  products: readonly FilterableProduct[],
): FilterOption[] => {
  if (options.length < 2) return options;
  if (category.valueSorting === 'ALPHA') {
    return [...options].sort((a, b) =>
      collator.compare(a.label ?? a.value, b.label ?? b.value),
    );
  }
  if (category.valueSorting === 'COUNT') {
    const counts = countProductsPerOption(products, { ...category, options });
    const countOf = (option: FilterOption) =>
      counts.get(optionKeyOf(option)) ?? 0;
    return [...options].sort((a, b) => countOf(b) - countOf(a));
  }
  return options;
};

/**
 * The groups the panel renders: derived options resolved, manual options
 * grouped, rows ordered by the category's `valueSorting`, and a group with no
 * options dropped entirely rather than shown empty (R1.4). A manual row no
 * product carries stays and renders greyed (docs/sort-and-filter.md §2).
 */
export const resolveFilterCategories = (
  categories: readonly FilterCategory[],
  products: readonly FilterableProduct[],
): FilterCategory[] =>
  categories
    .map((category) => ({
      ...category,
      options: sortFilterOptions(
        category,
        category.autoGenerateValues
          ? deriveFilterOptions(category, products)
          : groupFilterOptions(category.options),
        products,
      ),
    }))
    .filter((category) =>
      // A chips group carries chips instead of options and navigates instead of
      // filtering, so it needs neither options nor a `categoryId` — and cannot
      // have one: the field is hidden in chips mode, and Strapi strips a hidden
      // value on save (CMS-1893 R3.2).
      category.displayMode === 'CHIPS'
        ? category.chips.length > 0
        : !!category.categoryId && category.options.length > 0,
    );
